import React, {useEffect, useState, useMemo} from "react";
import { Grid, Typography, Button } from "@mui/material";
import { Grid as DataGrid, GridColumn } from "@progress/kendo-react-grid";
import { orderBy } from "@progress/kendo-data-query";
import {
  AccountTypeDropDownCell, FIAPayoutDropDownCell,
  TaxTypeDropDownCell,
} from "../account-tax-type-cells";
import { CurrencyCell, CurrencyCellMaxAmountFutureAdjustments } from "../currency-cell";
import { PercentageCell, AdvancedPercentageCell } from "../percentage-cell";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpOutline from "@mui/icons-material/HelpOutline";

import { DropDownList } from "@progress/kendo-react-dropdowns";
import AddIcon from "@mui/icons-material/Add";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { NONQUAL_ACCOUNTS, PRETAX_ACCOUNTS, TAXFREE_ACCOUNTS, FUNDING_SOURCE_CURRENT_INCOME, FUNDING_SOURCE_EXCESS_INCOME } from "../../shared/constants";
import YearCell from "../year-cell";
import Tooltip from '@mui/material/Tooltip';
import {
  collectErrorMessages,
  currencyFormatNoDecimals,
  getCurrentYear,
  isAccountRothConversionIntoFIA
} from "../../shared/shared";
import IconButton from "@mui/material/IconButton";
import ErrorIcon from "@mui/icons-material/Error";
import {StyledTooltip} from "../../pages/plan-inputs";
import ValidationSignCell from "../validation-sign-cell";
import OwnerDropDown from "../owner-cell";
import OwnerJointTrustDropDown from "../owner-joint-trust-cell";
import SwapVertIcon from '@mui/icons-material/SwapVert';

const CommandCell = (deleteFn) => {
  return (props) => {
    const dataItem = props.dataItem;

    return (
      <td>
        <DeleteIcon
          onClick={() => {
            deleteFn(dataItem);
          }}
        />
      </td>
    );
  };
};

const FiaPurposeDropDown = (props) => {
  const [state, setState] = useState({
    value: null,
    isDropdownOpen: false
  })

  const disabledGI = FiaPurposeDropDown.clientSpouseAge.client.age < 50 && (FiaPurposeDropDown.clientSpouseAge.client.hasSpouse ? FiaPurposeDropDown.clientSpouseAge.spouse.age < 49 : true);
  const options = [
    {
      value: "VOLATILITY_BUFFER",
      text: "Volatility Buffer",
      disabled: false
    },
    {
      value: "GUARANTEED_INCOME",
      text: "Guaranteed Income",
      disabled: disabledGI
    },
  ];

  const handleClose = () => {
    setState({ isDropdownOpen: false })
  }
  const handleOpen = () => {
    setState({ isDropdownOpen: true })
  }

  const itemRender = (element, { dataItem }) => {
    if (!dataItem || !dataItem.disabled) {
      return element;
    }

    const children = [
      <span
        key={dataItem.id}
        style={{
          color: '#ccc',
        }}
      >
        {dataItem.text}
      </span>,
    ];

    return React.cloneElement(
      element,
      {
        ...element.props,
        style: {
          ...element.props.style,
          cursor: 'default',
        },
      },
      children,
    );
  }

  const handleChange = (e) => {
    if (e.value.value === "GUARANTEED_INCOME") {
      if (options[1].disabled) return;
    }

    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value.value,
      });
    }
  };

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];

  if (dataItem.rila) {
    return <td>
      RILA
    </td>
  }

  return (
    <td>
      <DropDownList
        style={{
          width: "170px",
        }}
        onOpen={handleOpen}
        onClose={handleClose}
        itemRender={itemRender}
        dataItemKey={'value'}
        opened={state.isDropdownOpen}
        onChange={handleChange}
        value={options.find((c) => c.value === dataValue)}
        data={options}
        textField="text"
      />
    </td>
  );
};

export const RILAUpsideDropDown = (props) => {

  const handleChange = (e) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value.value,
      });

      setTimeout(() => {
        let defaultValue = 0;

        if (props.dataItem.rila) {
          defaultValue = e.target.value.value === "CAP" ? 0:50;
        } else {
          defaultValue = e.target.value.value === "CAP" ? 0:50;
        }

        props.onChange({
          dataIndex: 0,
          dataItem: props.dataItem,
          field: "upsideRate",
          syntheticEvent: e.syntheticEvent,
          value: defaultValue
        });
      }, 25);
    }
  };

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];

  const options = [
    {
      text: "Par",
      value: "PAR"
    },
    {
      text: "Cap",
      value: "CAP"
    }
  ];

  return <td>
    <DropDownList
      onChange={handleChange}
      value={options.find((c) => c.value === dataValue)}
      data={options}
      textField="text"
      valid={dataValue !== null && dataValue !== undefined && dataValue !== ""}

    />
  </td>
};

export const RILADownsideDropDown = (props) => {
  const handleChange = (e) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value.value,
      });

      setTimeout(() => {
        let defaultValue = 0;
        if (e.target.value.value === "PAR") {
          defaultValue = 50;
        } else if (e.target.value.value === "BUFFER") {
          defaultValue = 10;
        }

        props.onChange({
          dataIndex: 0,
          dataItem: props.dataItem,
          field: "downsideRate",
          syntheticEvent: e.syntheticEvent,
          value: defaultValue
        });
      }, 25);
    }
  };

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];

  const options = [
    {
      text: "Par",
      value: "PAR"
    },
    {
      text: "Floor",
      value: "FLOOR"
    },
    {
      text: "Buffer",
      value: "BUFFER"
    }
  ];

  return <td>
    <DropDownList
        onChange={handleChange}
        value={options.find((c) => c.value === dataValue)}
        data={options}
        textField="text"
        valid={dataValue !== null && dataValue !== undefined && dataValue !== ""}
    />
  </td>
};

export const AccountTransferDropDown = (accounts) => (props) => {
  const handleChange = (e) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value.value,
      });
    }
  };

  const getAccountTypeOptionOrDefault = (accountType, items) => {
    const result = items.find(e => e.value === accountType);
    if (!result) return items[0];
    return result;
  }

  let typeMap = {};
  const options = accounts.filter(item => {
    if (item.owner === "TRUST") {
      return props.dataItem.owner.toUpperCase() === item.trustName.toUpperCase() && item.id != null;
    }
    return item.owner === props.dataItem.owner && item.id != null;
  }).map((item) => {
    if (typeMap[props.dataItem.owner + item.taxType + item.value]) {
      typeMap[props.dataItem.owner + item.taxType + item.value].push(typeMap[props.dataItem.owner + item.taxType + item.value].length + 1);
    } else {
      typeMap[props.dataItem.owner + item.taxType + item.value] = [1];
    }
    return item;
  }).map((item) => {


    let option = null;

    if (item.taxType === "NON-QUALIFIED") {
      option = getAccountTypeOptionOrDefault(item.accountType, NONQUAL_ACCOUNTS);
    }

    if (item.taxType === "PRE-TAX") {
      option = getAccountTypeOptionOrDefault(item.accountType, PRETAX_ACCOUNTS);
    }

    if (item.taxType !== "NON-QUALIFIED" && item.taxType !== "PRE-TAX") {
      option = getAccountTypeOptionOrDefault(item.accountType, TAXFREE_ACCOUNTS);
    }

    if (option.value === "TE_OTHER" && item.accountTypeOtherName) {
      option.text = item.accountTypeOtherName;
    }

    let text = "";
    if(option != undefined) {
       text = option.text + " - " + currencyFormatNoDecimals(item.value)
    }
    if (typeMap[props.dataItem.owner + item.taxType + item.value].length == 1 && typeMap[props.dataItem.owner + item.taxType + item.value][0] == 1) {
      typeMap[props.dataItem.owner + item.taxType + item.value].shift();
    } else if (typeMap[props.dataItem.owner + item.taxType + item.value].length > 0) {
      text = text + " - " + typeMap[props.dataItem.owner + item.taxType + item.value][0];
      typeMap[props.dataItem.owner + item.taxType + item.value].shift();
    }
    return {
      value: item.id,
      text: text,
    };
  });

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];

  if (props.showCurrentIncomeFundingSource) {
    options.push({
      value: FUNDING_SOURCE_CURRENT_INCOME,
      text: "Current Income"
    });
  }

  if (props.showAlternativeFundingSources) {


    options.push({
      value: FUNDING_SOURCE_EXCESS_INCOME,
      text: "Excess Income"
    });
  }

  return (
    <td>
      <DropDownList
        onChange={handleChange}
        value={options.find((c) => c.value === dataValue)}
        data={options}
        textField="text"
        valid={dataValue !== null && dataValue !== undefined && dataValue !== ""}
      />
    </td>
  );
};

export const ConversionTypeDropDown = (props) => {

  const handleChange = (e) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value.value,
      });
    }
  };

  const options = [
    {
      value: "FIXED_AMOUNT",
      text: "Fixed Amount"
    },
    {
      value: "ALL",
      text: "All"
    }
  ];

  const { dataItem } = props;
  const field = props.field || "";

  if (!dataItem.isAccountRothConversionIntoFIA) {
    return <td></td>
  } else {
    if (dataItem.id == null && dataItem[field] === '') {
      dataItem[field] = "ALL";
    }
  }
  const dataValue = dataItem[field] === null ? "" : dataItem[field];

  return (
      <DropDownList
          onChange={handleChange}
          value={options.find((c) => c.value === dataValue)}
          data={options}
          textField="text"
          valid={dataValue !== null && dataValue !== undefined && dataValue !== ""}
      />
  );
}

const YearInputCell = (props) => {

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];

  return <td>

    <NumericTextBox
      value={dataItem.yearsOfDeferral}
      onChange={(e) => {
        FiaDetails.update("yearsOfDeferral")(dataItem, e.target.value);
      }}
      valid={YearInputCell.validation[dataItem.editIndex]?.yearsOfDeferral.valid}
    />
  </td>
}

const FiaDetails = (props) => {
  const data = [props.dataItem];

  const upsideRateInput = useMemo(() => {
    return AdvancedPercentageCell((item) => {
      if (item.upsideType === "PAR") {
        return item.upsideRate >= 0 && item.upsideRate <= 200;
      }

      return item.upsideRate >=0 && item.upsideRate <= 20;
    });
  }, []);

  return (
    <DataGrid
      data={data}
      scrollable={"none"}
      editField={"inEdit"}
      onItemChange={(event) => {
        const dataItem = event.dataItem;
        const field = event.field;
        const value = event.value;

        FiaDetails.update(field)(dataItem, value);
      }}
    >
      <GridColumn
        title={"Premium Bonus"}
        field={"premiumBonus"}
        cell={PercentageCell}
      />
      {data[0].annuityType !== "VOLATILITY_BUFFER" && (
          <GridColumn
              title={"Hypothetical Index Return"}
              field={"hypotheticalIndexReturn"}
              cell={PercentageCell}
          />
      )}
      {data[0].annuityType !== "VOLATILITY_BUFFER" && (
          <GridColumn field={"yearsOfDeferral"} title={"Years of Deferral"} cell={YearInputCell} />
      )}
      {data[0].annuityType !== "VOLATILITY_BUFFER" && (
        <GridColumn
          field={"baseRollUpRate"}
          title={"Base Roll Up Rate"}
          cell={PercentageCell}
        />
      )}
      {(data[0].annuityType !== "VOLATILITY_BUFFER" && data[0].payOutType === "INCREASING") && (
          <GridColumn
            field={"stackingMultiplier"}
            title={"Stacking Multiplier"}
            cell={PercentageCell}
          />
      )}
      {data[0].annuityType !== "VOLATILITY_BUFFER" && (
        <GridColumn
          field={"benefitBaseBonus"}
          title={"Benefit Base Bonus"}
          cell={PercentageCell}
        />
      )}
      {(data[0].annuityType !== "VOLATILITY_BUFFER" && data[0].payOutType === "INCREASING") && (
          <GridColumn
            field={"increasingGrowthMultiplier"}
            title={"Increasing Growth Multiplier"}
            cell={PercentageCell}
          />
      )}
      {data[0].annuityType !== "VOLATILITY_BUFFER" && (
        <GridColumn
          field={"payoutFactors"}
          title={"Payout Factors"}
          cell={PercentageCell}
        />
      )}
      {(data[0].annuityType !== "VOLATILITY_BUFFER" && data[0].payOutType !== "INCREASING") && (
          <GridColumn
              field={"payoutCola"}
              title={"Payout COLA"}
              cell={PercentageCell}
          />
      )}
      {data[0].annuityType === "VOLATILITY_BUFFER" && (<GridColumn
          title={"Upside"}
          field={"upsideType"}
          cell={RILAUpsideDropDown}
      />)}
      {data[0].annuityType === "VOLATILITY_BUFFER" && (<GridColumn
          title={" "}
          field={"upsideRate"}
          cell={upsideRateInput}
      />)}
      {data[0].annuityType === "VOLATILITY_BUFFER" && (<GridColumn
          title={"Downside"}
          field={"downsideType"}
          editable={false}
      />)}
      {data[0].annuityType === "VOLATILITY_BUFFER" && (<GridColumn
          title={" "}
          field={"downsideRate"}
          editable={false}
          format={"0.00%"}
      />)}
    </DataGrid>
  );
};

const RILADetails = (props) => {
  const data = [props.dataItem];

  const upsideRateInput = useMemo(() => {
    return AdvancedPercentageCell((item) => {
      if (item.upsideType === "PAR") {
        return item.upsideRate >= 0 && item.upsideRate <= 200;
      }

      return item.upsideRate >=0 && item.upsideRate <= 20;
    });
  }, []);

  const downsideRateInput = useMemo(() => {
    return AdvancedPercentageCell((item) => {
      if (item.downsideType === "PAR") {
        return item.downsideRate >= 0 && item.downsideRate <= 100;
      } else if (item.downsideType === "BUFFER") {
        return item.downsideRate >= 0 && item.downsideRate <= 30;
      }
      return item.downsideRate >= -30 && item.downsideRate <= 0;
    });
  }, []);

  return (
      <DataGrid
        data={data}
        scrollable={"none"}
        editField={"inEdit"}
        onItemChange={(event) => {
          const dataItem = event.dataItem;
          const field = event.field;
          const value = event.value;

          RILADetails.update(field)(dataItem, value);
        }}
      >
        <GridColumn
          title={"Upside"}
          field={"upsideType"}
          cell={RILAUpsideDropDown}
        />
        <GridColumn
          title={" "}
          field={"upsideRate"}
          cell={upsideRateInput}
        />
        <GridColumn
          title={"Downside"}
          field={"downsideType"}
          cell={RILADownsideDropDown}
        />
        <GridColumn
          title={" "}
          field={"downsideRate"}
          cell={downsideRateInput}
        />
      </DataGrid>
  )
}

const AccountTransferDetails = (props) => {
  const getTransferDetails = () => {
    return !props.dataItem.rila || props.dataItem.rila === undefined ? <FiaDetails {...props} /> : <RILADetails {...props} />;
  }

  return (
      <div className={"transfer-details"}>
        {getTransferDetails()}
      </div>
  );
};

const NoOfYearsCell = (props) => {

  const handleChange = (e) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value,
      });
    }
  };

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];

  if (!dataItem.isAccountRothConversionIntoFIA) return <td></td>

  return <td>
    <NumericTextBox
        value={dataValue}
        onChange={handleChange}
        valid={dataValue > 0}
    />
  </td>
}

const RothConversionIconCell = (props) => {
  if (!props.dataItem.isAccountRothConversionIntoFIA) {
    return <td></td>
  };

  return <td>
      <Tooltip title="Roth conversion into FIA" placement="top-start" style={{ paddingLeft: '2%' }}>
        <SwapVertIcon />
      </Tooltip>
    </td>
};

const Fia = (props) => {
  const [state, setState] = useState({
    sort: [
      {
        field: "firstName",
        dir: "asc",
      },
    ],
    page: { skip: 0, take: 10 },
    errorMessages: [],
  });

  useEffect(() => {
    setState({
      ...state,
      errorMessages: collectErrorMessages(props.validations.fiaTransfers, "fia transfer"),
    })
  }, [props.validations.fiaTransfers]);

  const addFia = () => {
    const prototype = {
      accountId: null,
      accountType: "BROKERAGE",
      annuityType: "VOLATILITY_BUFFER",
      rila: false,
      baseRollUpRate: 0,
      benefitBaseBonus: 0,
      hypotheticalIndexReturn: 0,
      payoutFactors: 0,
      payoutCola: 2,
      premiumBonus: 0,
      taxType: "NON-QUALIFIED",
      transferAmount: 0,
      transferEndYear: getCurrentYear(),
      transferStartYear: getCurrentYear(),
      transferId: null,
      yearsOfDeferral: 0,
      editIndex: 0,
      upsideType: "PAR",
      upsideRate: 50,
      downsideType: "FLOOR",
      downsideRate: 0,
      averageReturn: props.sandpReturns.averageReturn / 100,
      standardDeviation: props.sandpReturns.standardDeviation / 100,
      owner: "CLIENT",
      payOutType: "INCREASING",
      stackingMultiplier: 150,
      increasingGrowthMultiplier: 10,
      inEdit: true,
      isExpanded: true,
      fiaAccountId: null,
      noOfYears: 1,
      conversionType: null
    };

    props.addTransfer(prototype);
  };

  const addRILA = () => {
    const prototype = {
      accountId: null,
      accountType: "BROKERAGE",
      annuityType: "VOLATILITY_BUFFER",
      rila: true,
      baseRollUpRate: 0,
      benefitBaseBonus: 0,
      hypotheticalIndexReturn: 0,
      payoutFactors: 0,
      payoutCola: 2,
      premiumBonus: 0,
      taxType: "NON-QUALIFIED",
      payoutType: "INCREASING",
      transferAmount: 0,
      transferEndYear: getCurrentYear(),
      transferStartYear: getCurrentYear(),
      transferId: null,
      yearsOfDeferral: 0,
      editIndex: 0,
      upsideType: "PAR",
      upsideRate: 50,
      downsideType: "PAR",
      downsideRate: 50,
      averageReturn: props.sandpReturns.averageReturn / 100,
      standardDeviation: props.sandpReturns.standardDeviation / 100,
      owner: "CLIENT",
      inEdit: true,
      isExpanded: true,
      fiaAccountId: null,
      noOfYears: 1,
      conversionType: "FIXED_AMOUNT"
    };

    props.addTransfer(prototype);

  };

  const AccountTransferDDL = AccountTransferDropDown(props.accounts);
  YearInputCell.validation = props.validations.fiaTransfers
  FiaPurposeDropDown.clientSpouseAge = {
    client: props.client,
    spouse: props.spouse
  }
  OwnerDropDown.hasSpouse = props.client.hasSpouse;

  const onExpandChange = (event) => {
    props.update("isExpanded")(event.dataItem, !event.dataItem.isExpanded);
  };

  FiaDetails.update = props.update;
  RILADetails.update = props.update;

  const fiaTransfers = props.fiaTransfers.map((item, index) => {
    const validation = props.validations.fiaTransfers[index];
    let isValid = true;
    if (validation) {
      if (item.annuityType === "GUARANTEED_INCOME") {
        validation["transferEndYear"].valid = true;
      }

      Object.keys(validation).forEach((key) => {
        if (!validation[key].valid) isValid = false;
      });
    }
    return {
      ...item,
      inEdit: true,
      isValid: isValid,
      showJointTrustOptions: true,
      listTrusts: true,
      isAccountRothConversionIntoFIA: isAccountRothConversionIntoFIA(item, props.accounts),
    }
  });


  OwnerJointTrustDropDown.hasSpouse = props.client.hasSpouse;
  OwnerJointTrustDropDown.trustList = props.trustListing;

  const filterTrustList = (trustName) => {
    const findTrust = props.accounts.find(item => item.owner.toLowerCase() === trustName.toLowerCase());
    return findTrust !== undefined;
  };

  const shouldShowAlternativeFundingSources = (item) => {
    return (item.annuityType === "VOLATILITY_BUFFER" || item.annuityType === null) && (item.taxType === "NON_QUALIFIED" || item.taxType === "TAX_FREE" || item.taxType === "NON-QUALIFIED" || item.taxType === "TAX-FREE");
  }

  const shouldShowCurrentIncomeFundingSource = (item) => {
    return (item.annuityType === "VOLATILITY_BUFFER" || item.annuityType === null);
  }



  /*
  If GI FIA (Level or Increasing payout) is selected, then only allow the user to edit the Transfer Start Year

    Default Transfer Start Year to current year

    Make Transfer End Year = Transfer Start Year

    Grey out the Transfer End Year so that it is read-only (as currently designed.
   */

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography>FIA</Typography>
      </Grid>
      <Grid item xs={12}>
        {(!props.readOnly && state.errorMessages.length > 0) && (
            <div className="error-tooltip-wrapper">
              <StyledTooltip
                  arrow
                  className="error-tooltip"
                  placement="right"
                  title={
                    <ul className="error-tooltip-ul">{
                      state.errorMessages.map((e, idx) => {
                        return (
                            <li key={idx}>
                              <Typography
                                  className={'default-font'}
                                  textAlign={'left'}
                                  variant={'span'}> {e}
                              </Typography>
                            </li>
                        );
                      })}
                    </ul>
                  }
              >
                <IconButton>
                  <ErrorIcon />
                </IconButton>
              </StyledTooltip>
            </div>
        )}
        <Button onClick={addFia}>
          <AddIcon /> Add FIA
        </Button>
        <Button onClick={addRILA} className={"add-rila-button"}>
          <AddIcon />Add RILA
        </Button>
      </Grid>
      <Grid item xs={12}>
        <DataGrid
            className={"fia-transfer-table"}
          data={orderBy(fiaTransfers, state.sort).slice(
            state.page.skip,
            state.page.take + state.page.skip
          )}
          skip={state.page.skip}
          take={state.page.take}
          total={props.fiaTransfers.length}
          pageable={true}
          onPageChange={(event) => {
            setState({
              ...state,
              page: event.page,
            });
          }}
          scrollable={"none"}
          expandField={"isExpanded"}
          onExpandChange={onExpandChange}
          detail={AccountTransferDetails}
          editField={"inEdit"}
          onItemChange={(event) => {
            const dataItem = event.dataItem;
            const field = event.field;
            const value = event.value;

            props.update(field)(dataItem, value);
          }}
        >
          <GridColumn title={" "} field={"isValid"} cell={ValidationSignCell} />
          <GridColumn title={" "} field={""} cell={RothConversionIconCell} />
          <GridColumn title={"Owner"} field={"owner"} cell={(p) => <OwnerJointTrustDropDown {...p} allowAddTrust={false} filterFn={filterTrustList} />} />
          <GridColumn
            field={"taxType"}
            title={"Tax Type"}
            cell={TaxTypeDropDownCell}
          />
          <GridColumn
            field={"accountType"}
            title={"Account Type"}
            cell={AccountTypeDropDownCell}
          />
          <GridColumn
            field={"annuityType"}
            title={<Grid style={{ display: 'flex', alignItems: 'center' }}>
              FIA Purpose
              <Tooltip title="Guaranteed Income FIAs can only be added for clients at least 50 years old." placement="top-start" style={{ paddingLeft: '2%' }}>
                <HelpOutline className={"help-tool-tip"} />
              </Tooltip>
            </Grid>}
            cell={FiaPurposeDropDown}
          />
          <GridColumn
            field={"payOutType"}
            title={"Payout"}
            cell={FIAPayoutDropDownCell}
          />
          <GridColumn
            field={"accountId"}
            title={"Funding Source"}
            cell={(props) => <AccountTransferDDL {...props} showAlternativeFundingSources={shouldShowAlternativeFundingSources(props.dataItem)} showCurrentIncomeFundingSource={shouldShowCurrentIncomeFundingSource(props.dataItem)} />}
          />
          <GridColumn
            field={"transferAmount"}
            title={"Transfer Amount"}
            cell={CurrencyCellMaxAmountFutureAdjustments}
          />
          <GridColumn
            field={"transferStartYear"}
            title={"Transfer Start Year"}
            cell={YearCell}
          />
          <GridColumn
            field={"transferEndYear"}
            title={"Transfer End Year"}
            cell={YearCell}
          />
          <GridColumn
            field={"conversionType"}
            title={"Conversion Type"}
            cell={ConversionTypeDropDown}
          />
          <GridColumn
            field={"noOfYears"}
            title={"Number of years"}
            cell={NoOfYearsCell}
          />
          <GridColumn title={" "} cell={CommandCell(props.delete)} />
        </DataGrid>
      </Grid>
    </Grid>
  );
};

export default Fia;
